import React from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { i18n } from '@fs/zion-locale'
import { trackLink } from '@fs/zion-analytics'
import { boolAttr } from '@fs/zion-frontend-friends'
import NavLink from './NavLink'
import LanguageLink from './LanguageLink'
import useIsMobile from '../../hooks/useIsMobile'
import localeMapping from '../../usabilla'

const hasDocument = typeof document !== 'undefined'

const navCss = css`
  margin-bottom: 24px;
  text-align: center;
  &[is-mobile] {
    /* On mobile, they need a bit more space */
    line-height: 32px;
  }
`

const langPickerLinkCss = css`
  &::before {
    content: ' ';
    background: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAMAAAAoLQ9TAAAAWlBMVEUAAAB3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3d3cyECA3AAAAHXRSTlMApeAO+po1/H/uyXcZCvPortXBi04luZ9xVz4EE05v0OMAAACjSURBVBjTZU9JEoMwDJNDQgJZCDuU+v/frJ1OT9WMD14kWVD0zlhrXI8vOuKBjGVm6lo/8nDeNU119aNOSFYlLu8daWYSPiumsAO5Fu7hipd+b+QzWgdDcUkXFE5ODWx4wpqavBpZ+KBGJT7AVQ1boQCYmOcXgFsobsjAJsIJaumkhIOZVwiC2IL8BpglQw+pve5DPkQhB3n9F+5wkYYW7i/+B2iSCWl6HoSJAAAAAElFTkSuQmCC')
      top left no-repeat;
    padding-left: 24px;
    padding-bottom: 1px;
  }
`

const trustArcCss = css`
  z-index: 2;
`

const loadScript = (src) => {
  if (hasDocument) {
    const tag = document.createElement('script')
    tag.async = true
    tag.src = src
    document.getElementsByTagName('head')[0].appendChild(tag)
  }
}

export default function NavLinks({ onLangLinkClick = () => {}, ...props }) {
  const trustArcV2Ex = false
  const isMobile = useIsMobile()

  React.useEffect(() => {
    // TrustArc cookie prefs banner and overlay
    //
    // TODO: this needs to be addressed for SSR
    const isProd = typeof window !== 'undefined' && window.location.hostname === 'www.familysearch.org'
    let trustArcDomain = '//consent.trustarc.com'
    let trustArcDomainArg = 'familysearch.org'
    if (!trustArcV2Ex) {
      // if using v1
      trustArcDomain = isProd ? trustArcDomain : '//consent-icon-staging.truste.com'
    } else {
      // if using v2
      trustArcDomainArg = isProd ? 'familysearchv2.org' : 'familysearchtest.org'
    }
    let trusteLang = i18n.language
    if (trusteLang === 'zh') {
      trusteLang = 'zh-tw'
    } else if (trusteLang === 'zh-hans') {
      trusteLang = 'zh-cn'
    }
    const id = 'cookiePreferences'
    const scriptUrl = trustArcV2Ex
      ? `${trustArcDomain}/notice?domain=${trustArcDomainArg}&c=${id}&js=nj&noticeType=bb&text=true&gtm=1&pcookie&pn=1-0&cdn=1&language=${trusteLang}&cdn=1`
      : `${trustArcDomain}/notice?domain=${trustArcDomainArg}&c=${id}&js=bb&noticeType=bb&text=true&oc=1&fade=30000&language=${trusteLang}&cdn=1`

    loadScript(scriptUrl)
  }, [trustArcV2Ex])

  const { t } = useTranslation()

  // these extra links will lead to pages that are only in english. So don't show them unless the page is in english
  const isEnglishSpeaker = document.documentElement.lang === 'en'
  const openForm = () => {
    window.usabilla_live('setForm', localeMapping[i18n.language] || '')
    window.usabilla_live('click')
  }
  return (
    <>
      {/* trustArc banner needs this injection div */}
      {trustArcV2Ex && <div id="consent_blackbar" css={trustArcCss} />}
      {/* Without an aria-label, this is just listed as "navigation" to screenreaders (now lists as "Footer navigation") */}
      <nav aria-label={t('nav.footer', 'Footer')} css={navCss} is-mobile={boolAttr(isMobile)} {...props}>
        <div>
          <NavLink text={t('footer.feedback', 'Feedback')} onClick={openForm} data-link-name="footer_feedback" />
          <NavLink
            id="cookiePreferences"
            text={t('nav.cookie-preferences', 'Cookie Preferences')}
            onClick={trackLink}
            data-link-name="footer_cookie_preferences"
          />

          <NavLink data-link-name="menu-faq" onClick={trackLink} text={t('navigation.faq.label', 'FAQ')} to="/faq" />

          {isEnglishSpeaker && (
            <>
              <NavLink
                data-link-name="menu-store"
                onClick={trackLink}
                text={t('navigation.rootstech-store.label', 'RootsTech Store')}
                href="https://rootstech.mybrightsites.com/"
                external
                target="_blank"
              />

              <NavLink
                data-link-name="menu-previous-events"
                onClick={trackLink}
                text={t('navigation.previous-events.label', 'Previous Events')}
                href="https://www.rootstech.org/video-archive2"
                external
                target="_blank"
              />
            </>
          )}

          <LanguageLink onClick={onLangLinkClick} css={langPickerLinkCss} />
        </div>
      </nav>
    </>
  )
}
